import { queryOptions, useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";

import { consumablesAjax } from "../../ajax/ConsumablesAjax";
import { drillBlastReferenceAjax } from "../../ajax/DrillBlastReferenceAjax";
import { ErrorResponse } from "../../DTOs/Common/ErrorResponse";
import { ConsumableItemDTO, ConsumableStoreDTO, DrillComponentDTO, RigShiftDTO } from "../../DTOs/DrillBlast/ConsumablesDTO";
import { DrillBlastLookupDTO } from "../../DTOs/DrillBlast/DrillBlastLookupDTO";
import { TripleShift } from "../../types/Shift";

export const consumableStoresQuery = queryOptions({
  queryKey: ['drillBlast', 'consumableStores'],
  queryFn: async (): Promise<ConsumableStoreDTO[]> => {
    const res = await consumablesAjax.getStores();
    if (ErrorResponse.isErrorResponse(res)) throw new Error('Failed to load the stores');
    return res.data;
  },
  staleTime: 60 * 1000,
});

export const consumableItemsQuery = (includeInactive = false) => queryOptions({
  queryKey: ['references', 'drillBlast', 'consumableItems', includeInactive],
  queryFn: async (): Promise<ConsumableItemDTO[]> => {
    const res = await consumablesAjax.getItems(includeInactive);
    if (ErrorResponse.isErrorResponse(res)) throw new Error('Failed to load the catalogue');
    return res.data;
  },
  staleTime: 5 * 60 * 1000,
});

export const removalReasonsQuery = queryOptions({
  queryKey: ['references', 'drillBlast', 'removalReasons'],
  queryFn: async (): Promise<DrillBlastLookupDTO[]> => {
    const res = await drillBlastReferenceAjax.getRemovalReasons();
    if (ErrorResponse.isErrorResponse(res)) throw new Error('Failed to load the removal reasons');
    return res.data;
  },
  staleTime: 5 * 60 * 1000,
});

export const drillComponentsQuery = (includeRetired: boolean) => queryOptions({
  queryKey: ['drillBlast', 'drillComponents', includeRetired],
  queryFn: async (): Promise<DrillComponentDTO[]> => {
    const res = await consumablesAjax.getComponents(includeRetired);
    if (ErrorResponse.isErrorResponse(res)) throw new Error('Failed to load the components');
    return res.data;
  },
});

export const fittableComponentsQuery = (plantId: number) => queryOptions({
  queryKey: ['drillBlast', 'fittableComponents', plantId],
  queryFn: async (): Promise<DrillComponentDTO[]> => {
    const res = await consumablesAjax.getFittable(plantId);
    if (ErrorResponse.isErrorResponse(res)) throw new Error('Failed to load the components in stock');
    return res.data;
  },
});

export const rigShiftsQuery = (date: DateTime, shift: TripleShift) => queryOptions({
  queryKey: ['drillBlast', 'rigShifts', date.toISODate(), shift],
  queryFn: async (): Promise<RigShiftDTO[]> => {
    const res = await consumablesAjax.getRigShifts(date, shift);
    if (ErrorResponse.isErrorResponse(res)) throw new Error('Failed to load the rigs on shift');
    return res.data;
  },
});

const NONE: never[] = [];

export const useConsumableStores = () => useQuery(consumableStoresQuery).data ?? NONE;
export const useConsumableItems = (includeInactive = false) => useQuery(consumableItemsQuery(includeInactive)).data ?? NONE;
export const useRemovalReasons = () => useQuery(removalReasonsQuery).data ?? NONE;
